// Owner 2026-10-13: the legacy DMS editor (DMSNew.vb, a DevExpress rich-edit
// ribbon with File > Save As and Print) let people take the text they were
// writing out to Word, edit it there, and paste it back. The new editor had
// no way out, so long documents were hard to work on. This module builds a
// Word file (.doc, Word's own HTML flavour) or a print page (Save as PDF from
// the browser) from the editor's HTML, for one section or the whole document.
import { saveAs } from "file-saver";

const esc = (v) =>
    String(v ?? "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;")
        .replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const safeName = (v) => String(v || "document").replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();

/**
 * Quill writes alignment and indents as classes (ql-align-center, ql-indent-2);
 * Word reads inline styles only, so they are translated here. Tables made by
 * the DMS editor (dms-rt-table) and tables pasted from Excel keep their borders.
 */
export const quillHtmlToWordHtml = (html) => {
    let out = String(html ?? "");
    if (!out.trim() || out === "<p><br></p>") return "";
    out = out.replace(/class="([^"]*)"/g, (m, cls) => {
        const styles = [];
        const kept = [];
        cls.split(/\s+/).forEach((c) => {
            let mm;
            if ((mm = /^ql-align-(center|right|justify)$/.exec(c))) styles.push(`text-align:${mm[1]}`);
            else if ((mm = /^ql-indent-(\d)$/.exec(c))) styles.push(`margin-left:${Number(mm[1]) * 0.5}in`);
            else if (c) kept.push(c);
        });
        const parts = [];
        if (kept.length) parts.push(`class="${kept.join(" ")}"`);
        if (styles.length) parts.push(`style="${styles.join(";")}"`);
        return parts.join(" ");
    });
    return out;
};

const WORD_CSS = `
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.3; }
  h1 { font-size: 16pt; margin: 0 0 6pt; }
  h2 { font-size: 13pt; color: #1f3a8a; border-bottom: 1.5pt solid #1f3a8a; padding-bottom: 2pt; margin: 16pt 0 6pt; }
  h3 { font-size: 12pt; margin: 12pt 0 4pt; }
  p { margin: 0 0 6pt; }
  table { border-collapse: collapse; margin: 6pt 0; }
  td, th { border: 1pt solid #000; padding: 3pt 5pt; vertical-align: top; font-size: 10pt; }
  th { background: #e8ecf5; font-weight: bold; }
  img { max-width: 6.5in; }
  .meta { color: #555; font-size: 10pt; margin-bottom: 10pt; }
`;

/** One HTML page from {title, meta, sections:[{heading, html}]} */
const buildPage = ({ title, meta, sections }, forWord) => {
    const body = (sections || [])
        .map((s) => `<h2>${esc(s.heading)}</h2>\n${quillHtmlToWordHtml(s.html) || "<p>&nbsp;</p>"}`)
        .join("\n");
    const head = forWord
        ? `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${esc(title)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
<style>@page { size: 8.5in 11in; margin: 1in; } ${WORD_CSS}</style></head>`
        : `<html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>@page { size: letter; margin: 1in; } ${WORD_CSS} @media print { .no-print { display: none; } }</style></head>`;
    return `${head}
<body>
<h1>${esc(title)}</h1>
${meta ? `<div class="meta">${esc(meta)}</div>` : ""}
${body}
</body></html>`;
};

/** Download a .doc Word opens directly (its HTML flavour; tables, lists, images kept). */
export const exportToWord = ({ fileName, title, meta, sections }) => {
    const html = buildPage({ title, meta, sections }, true);
    const blob = new Blob(["﻿", html], { type: "application/msword;charset=utf-8" });
    saveAs(blob, `${safeName(fileName || title)}.doc`);
};

/**
 * Open the same page in a new window and start the browser's print dialog,
 * where "Save as PDF" is a printer. Returns false when the window was blocked.
 */
export const exportToPdf = ({ title, meta, sections }) => {
    const html = buildPage({ title, meta, sections }, false);
    const win = window.open("", "_blank");
    if (!win) return false;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    const go = () => { try { win.print(); } catch (_) { /* the page stays open to print by hand */ } };
    if (win.document.readyState === "complete") setTimeout(go, 250);
    else win.addEventListener("load", () => setTimeout(go, 250));
    return true;
};
